import {Form, FormFieldValidationResult} from '@mintdata-wsp/common/components/form/Form';
import {IFormFieldState} from '@mintdata-wsp/common/components/formField/FormField';

export interface IValidationFnProps<T>
{
    value?:T;
    state?:IFormFieldState<T>;
    form?:Form;
    errorMessage?:string;
}

export type ValidatorFn<T> = (props:IValidationFnProps<T>) => FormFieldValidationResult | Promise<FormFieldValidationResult>;

export class Validators
{
    public static async validate(props:IValidationFnProps<any>,
                                 validators:ValidatorFn<any>[]):Promise<FormFieldValidationResult>
    {
        let validationResult:FormFieldValidationResult;

        for( let validator of validators )
        {
            validationResult = await validator(props);

            if( validationResult.stopPropagation || !validationResult.isValid )
                return validationResult;
        }

        return validationResult || FormFieldValidationResult.validResult();
    }

    public static required(_errorMessage?:string)
    {
        return (props:IValidationFnProps<any>) =>
        {
            const {value, errorMessage = ''} = props;

            const isValid:boolean = !!value;
            const message:string = isValid ? '' : _errorMessage || errorMessage;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static equal(arg2:any, _errorMessage?:string)
    {
        return (props:IValidationFnProps<any>) =>
        {
            const {value, errorMessage = ''} = props;

            const isValid:boolean = value === arg2;
            const message:string = isValid ? '' : _errorMessage || errorMessage;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static notEqual(arg2:any, _errorMessage?:string)
    {
        return (props:IValidationFnProps<any>) =>
        {
            const {value, errorMessage = ''} = props;

            const isValid:boolean = value !== arg2;
            const message:string = isValid ? '' : _errorMessage || errorMessage;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static minLength(minLength:number, _errorMessage?:string)
    {
        return (props:IValidationFnProps<string>) =>
        {
            const {value, errorMessage} = props;

            const isValid:boolean = !!value && value.length >= minLength;
            const message:string = isValid ? '' : _errorMessage ||
                errorMessage && `${errorMessage} more than ${minLength - 1} symbols | ("${value.length}" - too short)`;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static maxLength(maxLength:number, _errorMessage?:string)
    {
        return (props:IValidationFnProps<string>) =>
        {
            const {value, errorMessage} = props;

            const isValid:boolean = !!value && value.length <= maxLength;
            const message:string = isValid ? '' : _errorMessage ||
                errorMessage && `${errorMessage} less than ${maxLength + 1} symbols | ("${value.length}" - too long)`;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static regexp(re:RegExp, _errorMessage?:string)
    {
        return (props:IValidationFnProps<string>) =>
        {
            const {value, errorMessage} = props;

            const isValid:boolean = !!value && re.test(value);
            const message:string = isValid ? '' : _errorMessage || errorMessage || '';

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static password(_errorMessage?:string)
    {
        return (props:IValidationFnProps<string>) =>
        {
            const {value, errorMessage = 'Please enter your password'} = props;

            const message = _errorMessage || errorMessage;

            return Validators.validate({value, errorMessage: message},
                [
                    Validators.required(),
                    Validators.minLength(8),
                    Validators.maxLength(255),
                ]);
        };
    }

    public static email(_errorMessage?:string)
    {
        return (props:IValidationFnProps<string>) =>
        {
            const {value, errorMessage = 'Please enter your email address'} = props;

            const message = _errorMessage || errorMessage;

            return Validators.validate({value, errorMessage: message},
                [
                    Validators.required(),

                    // http://emailregex.com/
                    Validators.regexp(
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        'Please enter valid email address'
                    ),
                ]);
        };
    }

    public static serverError(fieldName:string)
    {
        return (props:IValidationFnProps<any>) =>
        {
            const {form} = props;

            const hasServerError = form.getServerError(fieldName);

            const isValid:boolean = !hasServerError;
            const message:string = isValid ? '' : hasServerError;

            return new FormFieldValidationResult(isValid, message);
        };
    }

    public static emptyNotDirtyIsValid()
    {
        return (props:IValidationFnProps<any>) =>
        {
            const {value, state} = props;

            const {isDirty, isFocused, isSubmitted, isEditedAfterFocus} = state;

            // console.log('----------------------------------------');
            // console.log('state              0 =', value);
            //
            // console.log('isDirty            1 =', isDirty);
            // console.log('isFocused          1 =', isFocused);
            // console.log('isSubmitted        1 =', isSubmitted);
            // console.log('isEditedAfterFocus 1 =', isEditedAfterFocus);

            let stopPropagation = false;

            // console.log('stopPropagation    2 =', stopPropagation);

            if( !isSubmitted && (!isDirty || (isDirty && isFocused && isEditedAfterFocus)) )
                stopPropagation = true;

            // console.log('stopPropagation    3 =', stopPropagation);

            return new FormFieldValidationResult(true, '', stopPropagation);
        };
    }

    // TODO asyncSample
    // Validators.asyncSample(1000),
    // public static asyncSample(delay:number)
    // {
    //     return async (props:IValidationFnProps<any>):Promise<any> =>
    //     {
    //         return new Promise((resolve, reject) =>
    //         {
    //             setTimeout(() =>
    //             {
    //                 resolve( FormFieldValidationResult.validResult() );
    //             }
    //             , delay)
    //         });
    //     }
    // }
}
